<template>
  <div style="margin-bottom: 20px;" v-if="p">
    <Publication
      :publication="p"
      :condensed="true"
      :showAbstract="!actualFlow"
      :showReleaseNote="hasUpdate"
      :applications="applications"
      :hubs="hubs"
      :categories="categories"
      :topAttached="topAttached"
      :loadingPromise="loading"
      :key="'pub_' + p.identifier"
    />
    <div class="ui attached info message" v-if="hasUpdate" :key="'has_update_' + p.identifier">
      <i class="download icon" />
      {{ $t('HAS_UPDATE', { toVersion: p.releaseNote.version }) }}
    </div>
    <div class="ui attached warning message" v-if="hasChanges" :key="'has_changes_' + p.identifier">
      <i class="skull icon" />
      {{ $t('HAS_LOCAL_CHANGES') }}
    </div>
    <div
      class="ui bottom attached success message"
      v-if="actualFlow && !hasUpdate"
      :key="'already_dl_' + p.identifier"
    >
      <i class="checkmark icon" />
      {{ $t('ALREADY_NEWEST') }}
      <router-link
        v-if="!hideFlowLink"
        :to="{
          name: 'app-flow',
          params: { appId: appId, flowId: actualFlow.id, mode: 'settings' }
        }"
      >
        {{ $t('GOTO', { destination: actualFlow.name }) }}
      </router-link>
    </div>
    <div
      class="ui bottom attached secondary menu"
      v-if="!actualFlow || hasUpdate"
      :key="'menu_' + p.identifier"
    >
      <div class="right floated item">
        <div class="ui mini buttons">
          <div
            class="ui labeled icon button"
            @click="download(p)"
            :class="{ disabled: !appId }"
            v-if="!actualFlow"
          >
            <i class="download icon" />
            {{ $t('GET', { thing: $tc('FLOW', 1) }) }}
          </div>
          <template v-if="hasUpdate">
            <div
              v-if="!hasChanges"
              class="ui green labeled icon button"
              @click="download(p)"
              :class="{
                disabled: !hasUpdate || !appId
              }"
            >
              <i class="sync icon" />
              {{ $t('UPDATE', { thing: $tc('FLOW', 1) }) }}
            </div>
            <div
              v-if="hasChanges"
              class="ui yellow labeled icon button"
              :class="{ disabled: !appId }"
              v-confirm-click="{
                onConfirm: () => download(p),
                confirmHeader: $t('OVERWRITE_LOCAL_CHANGES_HEADER'),
                confirmMessage: $t('OVERWRITE_LOCAL_CHANGES_BODY')
              }"
            >
              <i class="sync icon" />
              {{ $t('UPDATE', { thing: $tc('FLOW', 1) }) }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
  <div style="margin-bottom: 20px;" class="ui bottom attached error message" v-else-if="hubErr">
    <i class="exclamation circle icon" />
    {{ $t('ERR_LOADING_FROM_HUB', { err: hubErr }) }}
  </div>
</template>

<style lang="scss" scoped></style>

<script>
// @flow
import Vue from 'vue'
import { mapMutations } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import { MutationTypes } from 'store'
import _ from 'lodash'
import Publication from './Publication'
import { Hub, Flow, Toast } from 'services'

export default {
  name: 'publication-getter',
  components: { Publication },

  props: ['appId', 'flow', 'publication', 'hideFlowLink', 'topAttached'],

  data() {
    return {
      pub: null,
      hubs: {},
      addedFlow: null,
      loading: null
    }
  },

  computed: {
    ...mapFields('flowEditor', ['blocklyXml', 'lastEditedBy', 'codeInEditor']),
    hubErr() {
      return this.$store.state.hub.loadingErr
    },
    actualFlow() {
      return this.addedFlow || this.flow
    },
    connections() {
      return this.$store.state.hub.connections
    },
    categories() {
      return _.keyBy(this.$store.state.hub.categories, 'identifier')
    },
    applications() {
      return _.keyBy(this.$store.state.hub.applications, 'identifier')
    },
    licenses() {
      return _.keyBy(this.$store.state.hub.licenses, 'identifier')
    },
    p() {
      return this.publication || this.pub
    },
    hasUpdate() {
      return (
        this.actualFlow &&
        this.actualFlow.publication?.releaseNote?.version !== this.p?.releaseNote?.version
      )
    },
    hasChanges() {
      return (
        this.actualFlow &&
        this.actualFlow.publication &&
        this.actualFlow.publication.hasLocalChanges &&
        this.actualFlow.publication.type !== Hub.INLINED_FLOW_PUBLICATION
      )
    },
    connection() {
      return _.find(this.connections, c => c.hub.id === this.hubs[(this.p?.origin?.hub)]?.hub?.id)
    }
  },

  methods: {
    ...mapMutations('flowEditor', ['updateFlowState']),
    download(p) {
      Hub.download(
        this.connection,
        p,
        this.actualFlow && this.actualFlow.version,
        this.appId,
        this.actualFlow
      )
        .then(downloadedFlow => {
          Toast.success(this.$i18n.t('WAS_INSTALLED', { thing: this.$i18n.tc('FLOW', 1) }))
          this.updateFlowState(downloadedFlow)
          this.codeInEditor = {
            code: downloadedFlow.code,
            es5Code: downloadedFlow.editorCode || downloadedFlow.code
          }
          this.blocklyXml = downloadedFlow.blocklyXml

          // Update our internal state
          this.addedFlow = downloadedFlow
        })
        .catch(err => {
          console.error(err)
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('WAS_NOT_INSTALLED', { thing: this.$i18n.tc('FLOW', 1) })
            })
          )
        })
    }
  },

  mounted() {
    Hub.list()

    this.loading = Hub.connections().then(conns => {
      return _.flatMap(
        Promise.allSettled(_.map(conns, Hub.about)).then(abouts => {
          this.hubs = _.keyBy(
            _.map(_.filter(abouts, a => a.status === 'fulfilled'), a => a.value.body),
            a => a.identifier
          )
        }),

        Promise.allSettled(_.flatMap(conns, Hub.latest)).then(pubs => {
          const items = _.flatMap(
            _.filter(pubs, p => p.status === 'fulfilled'),
            p => p.value && p.value.body && p.value.body.items
          )
          this.pub = _.find(
            items,
            item => item.identifier === this.actualFlow?.publication?.identifier
          )
        })
      )
    })
  }
}
</script>
